// Previous-month carryover panel — pinned above the current-month table to make
// past-due bills feel like a distinct pile that needs clearing.
import {
  Trash2, Zap, ExternalLink, CalendarClock, StickyNote,
} from "lucide-react";
import { formatMoney } from "@/lib/api";
import { Th, Td, EditableAmount, StatusDropdown } from "./table-cells";
import { shortDate } from "./utils";
import { MobileBillCard } from "./MobileBillCard";

export function CarryoverPanel({ bills, total, today, onEdit, onReschedule, onDelete, onDup, onStatus, onAmount }) {
  return (
    <section data-testid="carryover-panel" className="rounded-2xl border-2 border-[#F3E1CF] bg-[#FFFBF3] overflow-hidden">
      <div className="px-6 py-4 border-b border-[#F3E1CF] bg-[#FFF6ED] flex items-baseline justify-between flex-wrap gap-2">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-[#8C5D30]">Prior month bills</div>
          <h2 className="font-heading text-xl font-medium text-[#2C302E] mt-1">
            Unpaid from prior months
            <span className="ml-3 text-sm font-normal text-[#8C5D30]">({bills.length} bill{bills.length === 1 ? "" : "s"})</span>
          </h2>
        </div>
        <div className="text-sm text-[#59605C]">
          Total prior month bills <span className="font-heading font-semibold text-[#8C3A30] text-lg ml-1" data-testid="carryover-total">{formatMoney(total)}</span>
        </div>
      </div>
      <div className="hidden sm:block overflow-x-auto">
        <table className="w-full text-sm" data-testid="carryover-table">
          <thead className="bg-[#FFF6ED] text-[#8C5D30] sticky top-0 z-10">
            <tr>
              <Th className="w-28">Due date</Th>
              <Th>Bill</Th>
              <Th className="text-right w-36">Amount</Th>
              <Th className="w-36">Status</Th>
              <Th>Notes</Th>
              <Th className="w-24 text-right">Actions</Th>
            </tr>
          </thead>
          <tbody>
            {bills.map((b) => (
              <tr key={b.id} className="border-t border-[#F3E1CF] bg-white/60 hover:bg-white" data-testid={`carryover-row-${b.id}`}>
                <Td className="whitespace-nowrap font-medium text-[#2C302E]">
                  <button
                    type="button"
                    className="text-left hover:text-[#4A6148] hover:underline decoration-dotted underline-offset-2 focus:outline-none"
                    onClick={() => onReschedule(b)}
                    title="Click to reschedule this bill (this instance only)"
                    data-testid={`carryover-due-date-${b.id}`}
                  >
                    {shortDate(b.due_date)}
                  </button>
                </Td>
                <Td>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-[#2C302E] font-medium">{b.name}</span>
                    {b.notes && (
                      <span
                        className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#FEF3C7] text-[#7A5A1F]"
                        title={b.notes}
                        data-testid={`carryover-note-badge-${b.id}`}
                      >
                        <StickyNote size={11} />
                      </span>
                    )}
                    {b.pay_url && (
                      <a
                        href={b.pay_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-0.5 text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-[#EEF3EC] text-[#3B5B3E] hover:bg-[#D5E1D3]"
                        title="Open the online bill portal"
                        data-testid={`carryover-pay-online-${b.id}`}
                      >
                        <ExternalLink size={9} /> pay online
                      </a>
                    )}
                    {b.is_autopay && (
                      <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-[#EEF3EC] text-[#3B5B3E]">
                        <Zap size={9} /> autopay
                      </span>
                    )}
                    <span className="text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-[#FDF2F0] text-[#8C3A30]">prior month</span>
                    {b.original_due_date && (
                      <span
                        className="inline-flex items-center gap-0.5 text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-[#EDE7F6] text-[#4A3B7A]"
                        title={`Originally due ${b.original_due_date}`}
                        data-testid={`carryover-rescheduled-pill-${b.id}`}
                      >
                        <CalendarClock size={9} /> rescheduled
                      </span>
                    )}
                  </div>
                </Td>
                <Td className="text-right">
                  <EditableAmount bill={b} onSave={onAmount} testId={`carryover-amount-${b.id}`} />
                </Td>
                <Td><StatusDropdown bill={b} onChange={onStatus} /></Td>
                <Td className="text-[#59605C] text-xs max-w-[200px] truncate" title={b.notes || ""}>{b.notes || "—"}</Td>
                <Td className="text-right">
                  <div className="inline-flex items-center gap-1 justify-end flex-wrap">
                    <button className="btn-ghost !px-2 !py-1 text-xs" onClick={() => onEdit(b)} data-testid={`carryover-edit-${b.id}`}>
                      Edit
                    </button>
                    <button
                      className="btn-ghost !px-2 !py-1"
                      onClick={() => onReschedule(b)}
                      title="Reschedule this instance (doesn't change the recurring day)"
                      aria-label="Reschedule due date"
                      data-testid={`carryover-reschedule-${b.id}`}
                    >
                      <CalendarClock size={14} />
                    </button>
                    <button className="btn-ghost !px-2 !py-1" onClick={() => onDelete(b)} aria-label="Delete" data-testid={`carryover-del-${b.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Mobile cards */}
      <div className="sm:hidden space-y-2" data-testid="carryover-mobile-list">
        {bills.map((b) => (
          <MobileBillCard key={b.id} bill={b} isCarryover today={today}
            onEdit={onEdit} onReschedule={onReschedule} onDelete={onDelete} onDup={onDup} onStatus={onStatus} onAmount={onAmount} />
        ))}
      </div>
    </section>
  );
}
